/**
 * O que a pessoa quer receber: cards na tela, avisos fora da tela (#536).
 *
 * ── Duas escolhas, e por que separadas ───────────────────────────────────
 *
 * O card no canto e o aviso do sistema atendem momentos diferentes. Quem
 * trabalha com o CRM à vista pode achar o card uma interrupção a mais e
 * querer só o aviso de quando está em outra aba — ou o contrário: quem divide
 * a tela numa chamada não quer balão do sistema aparecendo para todo mundo.
 *
 * ── No navegador, e sincronizada entre abas ──────────────────────────────
 *
 * Como o "silenciar" do sino: é preferência da pessoa NESTE navegador — o
 * computador do balcão pode querer avisos que o notebook de casa não quer. A
 * escolha feita numa aba vale na hora nas outras, pelo evento `storage`, que
 * o navegador dispara nas abas que NÃO fizeram a mudança.
 *
 * ── Ligado por padrão ────────────────────────────────────────────────────
 *
 * Quem nunca escolheu recebe os dois. O aviso fora da tela continua
 * dependendo da permissão do navegador — a preferência ligada é "quero", não
 * "posso".
 */

import { useSyncExternalStore } from 'react';

export interface PreferenciasDeNotificacao {
  /** Os cards no canto superior direito. */
  readonly naTela: boolean;
  /** O aviso do sistema operacional, com o CRM em outra aba. */
  readonly foraDaTela: boolean;
}

export type Preferencia = keyof PreferenciasDeNotificacao;

const CHAVES: Readonly<Record<Preferencia, string>> = {
  naTela: 'cliqcrm:notificacoes-na-tela',
  foraDaTela: 'cliqcrm:notificacoes-fora-da-tela',
};

/**
 * `localStorage` pode lançar — navegação privada, armazenamento cheio,
 * política do navegador. Sem ele, vale o padrão, e a escolha só dura a aba.
 */
function lerChave(chave: string): string | null {
  try {
    return window.localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function lerDoNavegador(): PreferenciasDeNotificacao {
  return {
    naTela: lerChave(CHAVES.naTela) !== 'nao',
    foraDaTela: lerChave(CHAVES.foraDaTela) !== 'nao',
  };
}

/*
 * O retrato em cache: `useSyncExternalStore` compara por identidade, e um
 * objeto novo a cada leitura faria o React renderizar em laço.
 */
let atual: PreferenciasDeNotificacao | null = null;
const ouvintes = new Set<() => void>();

function avisar(): void {
  atual = lerDoNavegador();
  for (const ouvinte of ouvintes) ouvinte();
}

export function preferenciasDeNotificacao(): PreferenciasDeNotificacao {
  atual ??= lerDoNavegador();
  return atual;
}

/**
 * A pessoa já escolheu, ligando ou desligando? O padrão "ligado" de quem
 * nunca mexeu não conta como escolha — é o que o convite dos avisos pergunta.
 */
export function preferenciaDecidida(qual: Preferencia): boolean {
  return lerChave(CHAVES[qual]) !== null;
}

export function definirPreferencia(qual: Preferencia, ligada: boolean): void {
  try {
    window.localStorage.setItem(CHAVES[qual], ligada ? 'sim' : 'nao');
  } catch {
    // Sem armazenamento: a escolha vale só nesta aba, pelo cache abaixo.
    atual = { ...preferenciasDeNotificacao(), [qual]: ligada };
    for (const ouvinte of ouvintes) ouvinte();
    return;
  }
  avisar();
}

/** Outra aba mudou a preferência: o `storage` só chega às abas que não mudaram. */
function aoMudarEmOutraAba(evento: StorageEvent): void {
  if (evento.key === null || Object.values(CHAVES).includes(evento.key)) avisar();
}

function inscrever(ouvinte: () => void): () => void {
  if (ouvintes.size === 0) window.addEventListener('storage', aoMudarEmOutraAba);
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
    if (ouvintes.size === 0) window.removeEventListener('storage', aoMudarEmOutraAba);
  };
}

const PADRAO: PreferenciasDeNotificacao = { naTela: true, foraDaTela: true };

export function usePreferenciasDeNotificacao(): PreferenciasDeNotificacao {
  return useSyncExternalStore(inscrever, preferenciasDeNotificacao, () => PADRAO);
}
